import { observer } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import { useCreateBenefitSet } from '../hooks/useCreateBenefitSet';
import { useStepper } from '../../partials/actionStepperPartial';
import React, { useEffect, useState } from 'react';
import { EmployeeBenefitSetAssignmentTable } from '../../../../lib/components/tables/employee/employeeBenefitSetAssignment';
import { useActions } from '../../../../hooks/useActions';
import { useStore } from '../../../../hooks/useStore';
import { ConfirmationDialog, InformationDialog, Switch } from '../../../../lib/components';
import { Box, FormControl, Typography } from '@mui/material';
import { LoadingDialog } from '../../../benefits/customBenefitDialogs';
import { enqueueSnackbar } from 'notistack';
import { FeedbackAlert } from '../../../../lib/components/atomic/feedback';

export const EmployeeAssignmentSummary = observer(() => {
  const { t } = useTranslation();
  const { companyStore } = useStore();
  const { newBenefitSet } = useCreateBenefitSet();
  const { assignBenefitSet } = useCreateBenefitSet();
  const { setNextButtonConfig, activeStep, setActiveStep } = useStepper();
  const { selectedEmployees, setSelectedEmployees } = useActions();
  const [openBenefitSetAssignmentDialog, setOpenBenefitSetAssignmentDialog] = useState(false);
  const [openRetractBenefitsDialog, setOpenRetractBenefitsDialog] = useState(false);
  const [openOverwriteAssignmentsDialog, setOpenOverwriteAssignmentsDialog] = useState(false);
  const [legalCheck, setLegalCheck] = useState(false);
  const [isValidatingAssignments, setIsValidatingAssignments] = useState<boolean>(false);
  const [overwrittenAssignmentCount, setOverwrittenAssignmentCount] = useState<number>(0);

  const selectedEmployeeCount = Object.keys(selectedEmployees ?? {}).length;

  const benefitSet = companyStore.benefitSets?.find(
    (bs) => bs.id === newBenefitSet.editingBenefitSetId
  );
  const benefitSetName = benefitSet?.name_de ?? benefitSet?.name ?? '';

  const linkMapping = {
    '0': 'https://www.gesetze-im-internet.de/agg/',
    '2': 'https://support.pxtra.de/platform/kann-ich-meinen-mitarbeitenden-bereits-genutzte-benefits-wieder-entziehen'
  };

  const assignmentDialogContent = (
    <Box>
      <Typography sx={{ fontSize: '16px' }}>{t(`benefitSet:assignmentDialog:hint`)}</Typography>
      <Typography
        sx={{ paddingTop: 2.5, paddingBottom: 0.1, fontSize: '16px' }}
        variant="subtitle1"
      >
        {t(`benefitSet:assignmentDialog:heading`)}
      </Typography>
      <Typography component="div" sx={{ fontSize: '16px' }}>
        <ul className="list-disc pl-5 space-y-2">
          {(t('benefitSet:assignmentDialog:texts', { returnObjects: true }) as string[]).map(
            (text: string, index) => (
              <li key={text}>
                {text.includes('<0>') ? (
                  <Trans
                    i18nKey={text}
                    defaults={text}
                    components={[
                      <a
                        key={`link_agg_${text.substring(0, 20)}`}
                        href={
                          linkMapping[index.toString() as keyof typeof linkMapping] ??
                          'https://www.gesetze-im-internet.de/agg/'
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="text-heaven-600 no-underline"
                        aria-label="open link"
                      />
                    ]}
                  />
                ) : (
                  text
                )}
              </li>
            )
          )}
        </ul>
      </Typography>
      <FormControl
        sx={{
          '& .MuiFormControlLabel-label': {
            fontSize: '.875rem'
          },
          '& .MuiSwitch-root': {
            margin: '.25rem .75rem',
            marginLeft: 0
          }
        }}
      >
        <Switch
          label={t('benefitSet:assignmentDialog:confirmation')}
          labelPlacement={'end'}
          name="legalHint"
          onChange={(event, checked) => {
            setLegalCheck(checked);
          }}
        />
      </FormControl>
    </Box>
  );

  const dialogContent = (dialogType: string) => {
    const texts = t(`benefitSet:${dialogType}:texts`, {
      returnObjects: true
    }) as string[];

    return (
      <Box>
        <Typography sx={{ fontSize: '16px' }}>{t(`benefitSet:${dialogType}:hint`)}</Typography>

        <Typography variant="subtitle1" sx={{ pt: 2.5, pb: 0.1, fontSize: '16px' }}>
          {t(`benefitSet:${dialogType}:heading`)}
        </Typography>

        <Typography component="div" sx={{ fontSize: '16px' }}>
          <ul className="list-disc pl-5 space-y-2">
            {texts.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        </Typography>

        <FeedbackAlert
          severity="warning"
          description={t(`benefitSet:${dialogType}:warning`)}
          slim
        />
      </Box>
    );
  };

  async function submitAssignments() {
    const nextMonth = new Date(new Date().setMonth(new Date().getMonth() + 1, 1));
    setIsValidatingAssignments(true);
    newBenefitSet.assignedEmployees = Object.keys(selectedEmployees ?? {}).map((id) => String(id));
    try {
      const response = await assignBenefitSet(
        newBenefitSet.editingBenefitSetId ?? '',
        (newBenefitSet.startMonth ?? nextMonth.getMonth()) + 1,
        newBenefitSet.startYear ?? nextMonth.getFullYear()
      );
      if (
        response &&
        'confirmation_required' in response &&
        response.confirmation_required === true
      ) {
        if (
          response.message === 'There are employees who have further assignments in the future.'
        ) {
          setOverwrittenAssignmentCount(response.employee_count);
          setOpenOverwriteAssignmentsDialog(true);
        } else setOpenRetractBenefitsDialog(true);
        return;
      }

      if (response && 'errors' in response && response.errors && response.created) {
        const errorMessages = response.errors.map((err: { error: string }) => err.error).join(', ');
        enqueueSnackbar({
          variant: 'detailedError',
          message: t('benefitSet:assignPartialSuccess', {
            assignmentCount: response.created.length,
            errorCount: response.errors.length
          }),
          details: errorMessages
        });
        return;
      }
      setActiveStep(activeStep + 1);
      setSelectedEmployees({});
    } catch (error) {
      enqueueSnackbar({
        variant: 'detailedError',
        message: t('benefitSet:couldNotBeCreated'),
        details: error instanceof Error ? error.message : String(error)
      });
    } finally {
      setIsValidatingAssignments(false);
    }
  }

  useEffect(() => {
    setNextButtonConfig({
      text: t('common:ASSIGN'),
      disabled: Object.keys(selectedEmployees ?? {}).length === 0,
      onClick: async () => {
        setOpenBenefitSetAssignmentDialog(true);
      }
    });
  }, [selectedEmployees]);

  return (
    <>
      <div className="flex gap-x-8">
        <div>
          {t('benefitSet:assignmentSummary', {
            count: selectedEmployeeCount,
            benefitSet: benefitSetName
          })}
        </div>
      </div>
      <div style={{ marginTop: '24px' }}>
        <EmployeeBenefitSetAssignmentTable />
      </div>

      <InformationDialog
        open={openBenefitSetAssignmentDialog}
        content={assignmentDialogContent}
        title={t('benefitSet:assignBenefitSet')}
        confirmButtonText={t('benefitSet:assignNow')}
        confirmButtonDisabled={!legalCheck}
        cancelButtonText={t('common:CANCEL')}
        onConfirm={async () => {
          newBenefitSet.forceRetract = false;
          newBenefitSet.forceOverwrite = false;
          setOverwrittenAssignmentCount(0);
          await submitAssignments();
        }}
        onClose={() => {
          setOpenBenefitSetAssignmentDialog(false);
        }}
        onCancel={() => {
          setOpenBenefitSetAssignmentDialog(false);
        }}
      />

      <InformationDialog
        open={openRetractBenefitsDialog}
        content={dialogContent('retractBenefitsDialog')}
        title={t('benefitSet:retractBenefitsDialog:title')}
        confirmButtonText={t('benefitSet:retractBenefitsDialog:buttonText')}
        cancelButtonText={t('common:CANCEL')}
        onConfirm={async () => {
          newBenefitSet.forceRetract = true;
          await submitAssignments();
        }}
        onClose={() => {
          setOpenRetractBenefitsDialog(false);
        }}
        onCancel={() => setOpenRetractBenefitsDialog(false)}
      />

      <ConfirmationDialog
        open={openOverwriteAssignmentsDialog}
        variant={'overwrite'}
        title={t('common:ATTENTION')}
        confirmButtonText={t('benefitSet:overwriteAssignmentsDialog:buttonText')}
        contentComponent={
          <>
            <Typography marginBottom={2}>
              {t('benefitSet:overwriteAssignmentsDialog:text1', {
                count: overwrittenAssignmentCount
              })}
            </Typography>
            <Typography>{t('benefitSet:overwriteAssignmentsDialog:text2')}</Typography>
          </>
        }
        onConfirm={async () => {
          newBenefitSet.forceOverwrite = true;
          await submitAssignments();
        }}
        onCancel={() => setOpenOverwriteAssignmentsDialog(false)}
        onClose={() => setOpenOverwriteAssignmentsDialog(false)}
      />

      {isValidatingAssignments && (
        <LoadingDialog text={t('custom:validating_assignments')} open={isValidatingAssignments} />
      )}
    </>
  );
});
